import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, FloppyDisk, CurrencyDollar, DownloadSimple, Scales } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import SortableGrid from "../components/SortableGrid";

const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const usd = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
// Sales value in its own invoice currency (USD for exports, INR for domestic). "MIX"/unknown → plain number.
const money = (n, ccy) => {
  const c = (ccy || "").toUpperCase();
  if (c === "USD") return usd(n);
  if (c === "INR") return inr(n);
  return (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

// Cost field groups (keys must match backend CostIn)
const INDIA_INR = [
  ["india_port_charges_inr", "Port Charges"],
  ["india_demurrage_inr", "India Demurrage (if any)"],
  ["india_customs_inr", "India Customs Clearance & Fumigation"],
  ["export_insurance_inr", "Insurance (if any)"],
];
// Ports for the mandatory Port-name dropdown on the India inland rows.
const PORTS = ["ICD Dadri", "Mundra Port", "Nhava Sheva (JNPT)", "Not Applicable"];
// India-origin inland cost rows, each with a mandatory Port dropdown.

const COST_CAPTURE_FLOOR = "2026-04-01"; // FY26-27 — finance starts cost capture from 1 Apr 2026

// [amount key, port key, label]
const INDIA_INLAND = [
  ["india_inland_truck_inr", "india_inland_truck_port", "India Inland Cost by Truck"],
  ["india_inland_rail_inr", "india_inland_rail_port", "India Inland Cost by Rail"],
];
const USD_FIELDS = [
  ["sea_freight", "Sea Freight"],
  ["intl_inland", "International Inland"],
  ["customs_dest", "Customs Clearance (Destination)"],
  ["incidentals", "Incidentals"],
];
// Duty — Tariff type 1..3 (USD amount + own exchange rate, added to the shipment total).
const DUTY_FIELDS = [
  ["tariff1", "Tariff type 1"],
  ["tariff2", "Tariff type 2"],
  ["tariff3", "Tariff type 3"],
];
const DOMESTIC_INR = [
  ["dom_freight_inr", "Domestic Freight Cost"],
  ["dom_insurance_inr", "Insurance (if any)"],
];

const BLANK = {
  destination: "", shipment_type: "", ref_exch_rate: 0,
  dom_freight_inr: 0, dom_insurance_inr: 0,
  india_inland_truck_inr: 0, india_inland_truck_port: "",
  india_inland_rail_inr: 0, india_inland_rail_port: "",
  india_demurrage_inr: 0, india_customs_inr: 0, export_insurance_inr: 0,
  india_port_charges_inr: 0,
  sea_freight_usd: 0, sea_freight_rate: 0, intl_inland_usd: 0, intl_inland_rate: 0,
  customs_dest_usd: 0, customs_dest_rate: 0, incidentals_usd: 0, incidentals_rate: 0,
  tariff1_usd: 0, tariff1_rate: 0, tariff2_usd: 0, tariff2_rate: 0,
  tariff3_usd: 0, tariff3_rate: 0, duty_enabled: false, notes: "",
};

function computeTotals(f) {
  const isExp = f.shipment_type === "export";
  const inrKeys = isExp
    ? ["india_inland_truck_inr", "india_inland_rail_inr", "india_inland_inr",
       "india_port_charges_inr", "india_demurrage_inr", "india_customs_inr", "export_insurance_inr"]
    : ["dom_freight_inr", "dom_insurance_inr"];
  const inrParts = inrKeys.reduce((s, k) => s + (+f[k] || 0), 0);
  let usdParts = 0, usdToInr = 0;
  const usdList = isExp ? [...USD_FIELDS, ...(f.duty_enabled ? DUTY_FIELDS : [])] : [];
  const ref = +f.ref_exch_rate || 0;
  usdList.forEach(([b]) => {
    const u = +f[`${b}_usd`] || 0;
    usdParts += u;
    // blank/zero per-line rate → fall back to the ref rate (never convert a USD charge to ₹0)
    usdToInr += u * ((+f[`${b}_rate`] || 0) || ref);
  });
  return { total_inr: inrParts + usdToInr, total_usd: usdParts + (ref > 0 ? inrParts / ref : 0) };
}

// Zero the fields that don't belong to the given shipment type, so hidden values never leak
// into totals or the saved payload.
const clearForType = (t) => (t === "export"
  ? { dom_freight_inr: 0, dom_insurance_inr: 0 }
  : {
    india_inland_truck_inr: 0, india_inland_rail_inr: 0,
    india_inland_truck_port: "", india_inland_rail_port: "",
    india_demurrage_inr: 0, india_customs_inr: 0, export_insurance_inr: 0,
    india_port_charges_inr: 0,
    sea_freight_usd: 0, sea_freight_rate: 0, intl_inland_usd: 0, intl_inland_rate: 0,
    customs_dest_usd: 0, customs_dest_rate: 0, incidentals_usd: 0, incidentals_rate: 0,
    tariff1_usd: 0, tariff1_rate: 0, tariff2_usd: 0, tariff2_rate: 0, tariff3_usd: 0, tariff3_rate: 0,
  });

const Num = ({ value, onChange, testid, placeholder }) => (
  <input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    placeholder={placeholder || "0"} style={{ width: "100%", padding: "7px 9px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, textAlign: "right" }} />
);

// ── Capture tab ───────────────────────────────────────────────────────────
function CaptureTab() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);        // header
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [savingWhich, setSavingWhich] = useState("");   // "partial" | "full" while that button saves
  const [frm, setFrm] = useState(COST_CAPTURE_FLOOR); // invoice-date from (default 1 Apr 2026)
  const [to, setTo] = useState("");                   // invoice-date to
  const [pcode, setPcode] = useState("");             // customer filter
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | partial | full

  const runSearch = useCallback(async (term, f, t, pc) => {
    try {
      const p = new URLSearchParams({ q: term || "", limit: "4000" });
      if (f) p.set("from", f);
      if (t) p.set("to", t);
      if (pc) p.set("pcode", pc);
      const { data } = await api.get(`/logistics/invoices?${p.toString()}`);
      setResults(data || []);
    } catch { setResults([]); }
  }, []);

  // Customer dropdown options for the selected date window.
  useEffect(() => {
    const p = new URLSearchParams();
    if (frm) p.set("from", frm);
    if (to) p.set("to", to);
    api.get(`/logistics/customers?${p.toString()}`).then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
  }, [frm, to]);

  useEffect(() => { runSearch("", frm, to, pcode); }, [runSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => runSearch(q, frm, to, pcode), 350);
    return () => clearTimeout(t);
  }, [q, frm, to, pcode, runSearch]);


  const pick = async (sale_no) => {
    try {
      const { data } = await api.get(`/logistics/invoice/${sale_no}`);
      setSel(data.header);
      setLines(data.lines || []);
      const d = data.defaults || {};
      const base = { ...BLANK, ...(data.cost || {}) };
      // prefill from customer defaults + invoice rate when no saved cost yet
      if (!data.cost) {
        base.destination = d.destination || "";
        base.shipment_type = d.shipment_type || (data.header.is_export ? "export" : "domestic");
        base.ref_exch_rate = d.ref_exch_rate || data.header.invoice_exch_rate || 0;
        ["sea_freight_rate", "intl_inland_rate", "customs_dest_rate", "incidentals_rate"].forEach((k) => {
          base[k] = d[k] || data.header.invoice_exch_rate || 0;
        });
      }
      if (!base.shipment_type) base.shipment_type = data.header.is_export ? "export" : "domestic";
      setForm(base);
    } catch { toast.error("Could not load invoice"); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setType = (v) => setForm((f) => ({ ...f, shipment_type: v, ...clearForType(v) }));
  const totals = useMemo(() => computeTotals(form), [form]);
  const isExport = form.shipment_type === "export";

  const save = async (status = "full") => {
    if (!sel) return;
    // Mandatory checks only enforced on a FULL (final) save. A partial draft may be incomplete.
    if (status === "full" && isExport) {
      if ((+form.india_inland_truck_inr || 0) > 0 && !form.india_inland_truck_port) {
        toast.error("Select a Port name for India Inland Cost by Truck"); return;
      }
      if ((+form.india_inland_rail_inr || 0) > 0 && !form.india_inland_rail_port) {
        toast.error("Select a Port name for India Inland Cost by Rail"); return;
      }
      if (form.duty_enabled) {
        const anyDuty = [form.tariff1_usd, form.tariff2_usd, form.tariff3_usd]
          .some((v) => (+v || 0) > 0);
        if (!anyDuty) {
          toast.error("Duty is turned on — enter at least one Tariff amount, or turn Duty off");
          return;
        }
      }
    }
    setSaving(true); setSavingWhich(status);
    try {
      const payload = { sale_no: sel.sale_no, ...form, ...clearForType(form.shipment_type), save_status: status };
      // Duty off → don't persist any tariff amounts
      if (!form.duty_enabled) {
        ["tariff1_usd", "tariff1_rate", "tariff2_usd", "tariff2_rate", "tariff3_usd", "tariff3_rate"]
          .forEach((k) => { payload[k] = 0; });
      }
      Object.keys(payload).forEach((k) => { if (typeof payload[k] === "string" && /_(inr|usd|rate)$/.test(k)) payload[k] = +payload[k] || 0; });
      const { data } = await api.post("/logistics/cost", payload);
      toast.success(status === "partial"
        ? `Partially saved — draft kept out of Cost Analysis (${inr(data.total_inr)})`
        : `Fully saved — ${inr(data.total_inr)} now flows to Cost Analysis`);
      runSearch(q, frm, to, pcode);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); setSavingWhich(""); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 18 }}>
      {/* invoice picker */}
      <div>
        {/* invoice-date + customer filters */}
        <div style={{ display: "grid", gap: 8, marginBottom: 10, padding: "10px 11px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10 }} data-testid="logistics-capture-filters">
          <div>
            <div style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Invoice Date</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} data-testid="logistics-capture-from"
                style={{ flex: 1, padding: "6px 8px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12.5 }} />
              <span style={{ color: "#94A3B8" }}>→</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="logistics-capture-to"
                style={{ flex: 1, padding: "6px 8px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12.5 }} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Customer</div>
            <select value={pcode} onChange={(e) => setPcode(e.target.value)} data-testid="logistics-capture-customer"
              style={{ width: "100%", padding: "6px 8px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12.5, background: "#fff" }}>
              <option value="">All customers</option>
              {customers.map((c) => <option key={c.pcode} value={c.pcode}>{c.customer_name}</option>)}
            </select>
          </div>
          {(frm !== COST_CAPTURE_FLOOR || to || pcode) && (
            <button onClick={() => { setFrm(COST_CAPTURE_FLOOR); setTo(""); setPcode(""); }} data-testid="logistics-capture-clear"
              className="jde-btn" style={{ fontSize: 11.5, justifySelf: "start" }}>Reset filters</button>
          )}
        </div>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <MagnifyingGlass size={15} weight="bold" color="#94A3B8" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice # / customer…" data-testid="logistics-invoice-search"
            style={{ width: "100%", padding: "9px 12px 9px 32px", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 13 }} />
        </div>
        {(() => {
          const capturedCount = results.filter((r) => r.captured).length;
          const pendingCount = results.length - capturedCount;
          const partialCount = results.filter((r) => r.save_status === "partial").length;
          const fullCount = results.filter((r) => r.captured && r.save_status !== "partial").length;
          const pct = results.length ? Math.round((capturedCount / results.length) * 100) : 0;
          const matchStatus = (r) => (
            statusFilter === "pending" ? !r.captured :
            statusFilter === "partial" ? r.save_status === "partial" :
            statusFilter === "full" ? (r.captured && r.save_status !== "partial") :
            true);
          const shown = results.filter(matchStatus);
          const custName = pcode ? (customers.find((c) => c.pcode === pcode)?.customer_name
                                    || results[0]?.customer_name || "") : "";
          return (
            <>
              {/* progress tracker (scoped to the current customer/date filter) */}
              <div style={{ marginBottom: 8 }} data-testid="logistics-capture-progress">
                <div style={{ fontSize: 11, fontWeight: 800, color: "#0369A1", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }} data-testid="logistics-progress-scope">
                  {custName ? custName : "All customers"}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11.5, marginBottom: 4 }}>
                  <span style={{ color: "#0F172A", fontWeight: 700 }} data-testid="logistics-filled-count">{capturedCount} of {results.length} filled ({pct}%)</span>
                  <span style={{ color: pendingCount ? "#B45309" : "#059669", fontWeight: 700 }} data-testid="logistics-pending-count">
                    {pendingCount ? `${pendingCount} pending` : "All filled"}
                  </span>
                </div>
                <div style={{ height: 6, background: "#E2E8F0", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "#059669" : "#0EA5E9", transition: "width .3s" }} />
                </div>
                <div style={{ display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap" }} data-testid="logistics-status-filter">
                  {[
                    { k: "all", label: `All (${results.length})`, c: "#0F172A" },
                    { k: "pending", label: `Pending (${pendingCount})`, c: "#92400E" },
                    { k: "partial", label: `Partial (${partialCount})`, c: "#075985" },
                    { k: "full", label: `Full (${fullCount})`, c: "#065F46" },
                  ].map((o) => (
                    <button key={o.k} data-testid={`logistics-filter-${o.k}`} onClick={() => setStatusFilter(o.k)}
                      style={{ padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer",
                        border: "1px solid " + (statusFilter === o.k ? o.c : "#E2E8F0"),
                        background: statusFilter === o.k ? o.c : "#fff",
                        color: statusFilter === o.k ? "#fff" : o.c }}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 6 }} data-testid="logistics-invoice-count">
                {shown.length} invoice{shown.length === 1 ? "" : "s"}{statusFilter !== "all" ? ` · ${statusFilter}` : ""}
              </div>
              <div style={{ maxHeight: 520, overflowY: "auto", display: "grid", gap: 6 }}>
                {shown.map((r) => (
                  <button key={r.sale_no} data-testid={`logistics-invoice-${r.sale_no}`} onClick={() => pick(r.sale_no)}
                    style={{ textAlign: "left", padding: "9px 11px",
                      border: "1px solid " + (sel?.sale_no === r.sale_no ? "#0EA5E9" : (r.save_status === "partial" ? "#38BDF8" : (r.captured ? "#E2E8F0" : "#FCD34D"))),
                      borderLeft: "4px solid " + (r.save_status === "partial" ? "#0EA5E9" : (r.captured ? "#059669" : "#F59E0B")),
                      borderRadius: 9, background: sel?.sale_no === r.sale_no ? "#F0F9FF" : (r.save_status === "partial" ? "#F0F9FF" : (r.captured ? "#fff" : "#FFFBEB")), cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 13, color: "#0F172A" }}>
                      <span>#{r.inv_no}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ color: r.is_export ? "#0369A1" : "#7C3AED" }}>{r.is_export ? "EXPORT" : "DOMESTIC"}</span>
                        <span data-testid={`logistics-status-${r.sale_no}`} style={{
                          padding: "1px 7px", borderRadius: 999, fontSize: 9.5, letterSpacing: 0.3,
                          color: r.save_status === "partial" ? "#075985" : (r.captured ? "#065F46" : "#92400E"),
                          background: r.save_status === "partial" ? "#E0F2FE" : (r.captured ? "#D1FAE5" : "#FEF3C7") }}>
                          {r.save_status === "partial" ? "PARTIAL" : (r.captured ? "FULL" : "PENDING")}
                        </span>
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: "#64748B" }}>{r.customer_name} · {r.currency_code} {r.amount?.toLocaleString()}</div>
                    {r.sale_date && <div style={{ fontSize: 10.5, color: "#94A3B8", marginTop: 1 }}>Inv date: {r.sale_date}</div>}
                  </button>
                ))}
                {shown.length === 0 && <div className="muted" style={{ padding: 12, fontSize: 12 }}>{statusFilter !== "all" ? `No ${statusFilter} invoices.` : "No invoices."}</div>}
              </div>
            </>
          );
        })()}
      </div>

      {/* form */}
      <div>
        {!sel ? (
          <div className="muted" style={{ padding: 40, textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 12 }} data-testid="logistics-empty">
            Select an invoice to enter logistics costs.
          </div>
        ) : (
          <div data-testid="logistics-form">
            {/* header */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, padding: "14px 16px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, marginBottom: 14 }}>
              <Hdr k="Invoice #" v={`#${sel.inv_no}`} />
              <Hdr k="Customer" v={sel.customer_name} />
              <Hdr k="Invoice Value" v={`${sel.currency_code} ${sel.invoice_value?.toLocaleString()}`} />
              <Hdr k="Total Qty" v={sel.invoice_qty?.toLocaleString()} />
              <div>
                <div style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase" }}>Shipment Type</div>
                <select value={form.shipment_type} onChange={(e) => setType(e.target.value)} data-testid="logistics-shipment-type"
                  style={{ padding: "5px 8px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, marginTop: 2 }}>
                  <option value="domestic">Domestic</option>
                  <option value="export">Export</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase" }}>Destination</div>
                <input value={form.destination} onChange={(e) => set("destination", e.target.value)} data-testid="logistics-destination" placeholder="e.g. Long Beach, USA"
                  style={{ width: "100%", padding: "5px 8px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, marginTop: 2 }} />
              </div>
            </div>

            {/* cost sections */}
            {!isExport ? (
              <Section title="Domestic Costs (INR)">
                {DOMESTIC_INR.map(([k, label]) => (
                  <Row key={k} label={label}><Num value={form[k]} onChange={(v) => set(k, v)} testid={`logistics-${k}`} /></Row>
                ))}
              </Section>
            ) : (
              <>
                <Section title="India Origin Costs (INR)">
                  {INDIA_INLAND.map(([amtKey, portKey, label]) => {
                    const needsPort = (+form[amtKey] || 0) > 0 && !form[portKey];
                    return (
                      <div key={amtKey} style={{ marginBottom: 10 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 10, alignItems: "center" }}>
                          <div style={{ fontSize: 13, color: "#334155" }}>{label}</div>
                          <Num value={form[amtKey]} onChange={(v) => set(amtKey, v)} testid={`logistics-${amtKey}`} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 10, alignItems: "center", marginTop: 6 }}>
                          <div style={{ fontSize: 12, color: "#64748B", paddingLeft: 12 }}>
                            Port name <span style={{ color: "#DC2626" }}>*</span>
                          </div>
                          <select
                            value={form[portKey] || ""}
                            onChange={(e) => set(portKey, e.target.value)}
                            data-testid={`logistics-${portKey}`}
                            style={{
                              width: "100%", padding: "7px 9px", fontSize: 13,
                              border: "1px solid " + (needsPort ? "#DC2626" : "#E2E8F0"),
                              borderRadius: 8, background: "#fff",
                            }}
                          >
                            <option value="">Select port…</option>
                            {PORTS.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  {INDIA_INR.map(([k, label]) => (
                    <Row key={k} label={label}><Num value={form[k]} onChange={(v) => set(k, v)} testid={`logistics-${k}`} /></Row>
                  ))}
                </Section>
                <Section title="Freight / Destination Costs (USD + exchange rate)">
                  <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", gap: 8, fontSize: 10.5, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", padding: "0 2px 4px" }}>
                    <div>Component</div><div style={{ textAlign: "right" }}>Amount (USD)</div><div style={{ textAlign: "right" }}>Exch. Rate</div>
                  </div>
                  {USD_FIELDS.map(([b, label]) => (
                    <div key={b} style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", gap: 8, alignItems: "center", marginBottom: 6 }}>
                      <div style={{ fontSize: 13, color: "#334155" }}>{label}</div>
                      <Num value={form[`${b}_usd`]} onChange={(v) => set(`${b}_usd`, v)} testid={`logistics-${b}_usd`} />
                      <Num value={form[`${b}_rate`]} onChange={(v) => set(`${b}_rate`, v)} testid={`logistics-${b}_rate`} />
                    </div>
                  ))}
                </Section>
                <Section title={
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <span>Duty (USD + exchange rate)</span>
                    <button
                      type="button"
                      data-testid="logistics-duty-toggle"
                      onClick={() => set("duty_enabled", !form.duty_enabled)}
                      aria-pressed={!!form.duty_enabled}
                      style={{
                        position: "relative", width: 42, height: 22, borderRadius: 999, border: "none",
                        cursor: "pointer", transition: "background-color .18s",
                        background: form.duty_enabled ? "#0EA5E9" : "#CBD5E1", flex: "0 0 auto",
                      }}
                    >
                      <span style={{
                        position: "absolute", top: 2, left: form.duty_enabled ? 22 : 2, width: 18, height: 18,
                        borderRadius: "50%", background: "#fff", transition: "left .18s",
                        boxShadow: "0 1px 2px rgba(0,0,0,.25)",
                      }} />
                    </button>
                  </div>
                }>
                  {!form.duty_enabled ? (
                    <p data-testid="logistics-duty-off" style={{ fontSize: 12.5, color: "#94A3B8", margin: "2px 2px 0" }}>
                      No duty on this invoice. Turn on the toggle to capture Tariff type 1–3.
                    </p>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", gap: 8, fontSize: 10.5, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", padding: "0 2px 4px" }}>
                        <div>Component</div><div style={{ textAlign: "right" }}>Amount (USD)</div><div style={{ textAlign: "right" }}>Exch. Rate</div>
                      </div>
                      {DUTY_FIELDS.map(([b, label]) => (
                        <div key={b} style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", gap: 8, alignItems: "center", marginBottom: 6 }}>
                          <div style={{ fontSize: 13, color: "#334155" }}>{label}</div>
                          <Num value={form[`${b}_usd`]} onChange={(v) => set(`${b}_usd`, v)} testid={`logistics-${b}_usd`} />
                          <Num value={form[`${b}_rate`]} onChange={(v) => set(`${b}_rate`, v)} testid={`logistics-${b}_rate`} />
                        </div>
                      ))}
                    </>
                  )}
                </Section>
              </>
            )}

            <Section title="Grand Total">
              <Row label="Reference exchange rate (₹/$ for USD total)"><Num value={form.ref_exch_rate} onChange={(v) => set("ref_exch_rate", v)} testid="logistics-ref_exch_rate" /></Row>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "10px 2px 2px" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase" }}>Final Logistics Cost (INR)</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: "#0F172A" }} data-testid="logistics-total-inr">{inr(totals.total_inr)}</span>
              </div>
            </Section>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
              <button className="jde-btn" onClick={() => save("partial")} disabled={saving} data-testid="logistics-save-partial"
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <FloppyDisk size={16} weight="bold" />{savingWhich === "partial" ? "Saving…" : "Partially Save"}
              </button>
              <button className="jde-btn jde-btn-primary" onClick={() => save("full")} disabled={saving} data-testid="logistics-save-full"
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <FloppyDisk size={16} weight="bold" />{savingWhich === "full" ? "Saving…" : "Fully Save Logistics Cost"}
              </button>
              <span style={{ fontSize: 11.5, color: "#94A3B8" }}>
                Only <b style={{ color: "#059669" }}>fully saved</b> costs flow to Cost Analysis. Partial saves are drafts.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Hdr = ({ k, v }) => (
  <div><div style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase" }}>{k}</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{v}</div></div>
);
const Section = ({ title, children }) => (
  <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: "12px 16px", marginBottom: 14, background: "#fff" }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{title}</div>
    {children}
  </div>
);
const Row = ({ label, children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 10, alignItems: "center", marginBottom: 7 }}>
    <div style={{ fontSize: 13, color: "#334155" }}>{label}</div>{children}
  </div>
);

// ── Analysis tab ────────────────────────────────────────────────────────────
function AnalysisTab() {
  const [data, setData] = useState(null);
  const [items, setItems] = useState(null);
  const [basis, setBasis] = useState("value");
  const [pkSale, setPkSale] = useState(null);
  const [mwOpen, setMwOpen] = useState(false);
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const range = useMemo(() => {
    const p = new URLSearchParams();
    if (frm) p.set("from", frm);
    if (to) p.set("to", to);
    return p.toString();
  }, [frm, to]);
  useEffect(() => {
    api.get(`/logistics/analysis${range ? "?" + range : ""}`).then((r) => setData(r.data)).catch(() => setData({ shipments: [], by_customer: [], totals: {} }));
  }, [range]);
  useEffect(() => {
    setItems(null);
    const p = new URLSearchParams(range);
    p.set("basis", basis);
    api.get(`/logistics/by-item?${p.toString()}`).then((r) => setItems(r.data)).catch(() => setItems({ items: [], basis }));
  }, [basis, range]);
  if (!data) return <div className="muted" style={{ padding: 24 }}>Loading…</div>;
  const byWeight = basis === "weight" && items && items.basis === "weight";

  const itemCols = [
    { key: "part", header: "Item", sortable: true, render: (r) => <span>{r.part}{r.name ? ` · ${r.name}` : ""}</span>, searchAccessor: (r) => `${r.part} ${r.name || ""}` },
    { key: "qty", header: "Qty", align: "right", sortable: true, render: (r) => r.qty?.toLocaleString() },
    { key: "weight", header: "Weight (kg)", align: "right", sortable: true, render: (r) => r.weight ? r.weight.toLocaleString() : "—" },
    { key: "logistics_inr", header: "Freight (₹)", align: "right", sortable: true, render: (r) => inr(r.logistics_inr) },
    { key: "duty_inr", header: "Duty (₹)", align: "right", sortable: true, render: (r) => inr(r.duty_inr) },
    { key: "landed_inr", header: "Landed (₹)", align: "right", sortable: true, render: (r) => inr(r.landed_inr) },
    { key: "sales_value_native", header: "Sales Value", align: "right", sortable: true, render: (r) => money(r.sales_value_native, r.sales_currency) },
    byWeight
      ? { key: "per_kg_inr", header: "Freight ₹/kg", align: "right", sortable: true, render: (r) => inr(r.per_kg_inr) }
      : { key: "per_unit_inr", header: "Freight ₹/unit", align: "right", sortable: true, render: (r) => inr(r.per_unit_inr) },
  ];
  const byWeightCust = basis === "weight" && data.weight_available;
  const dlXlsx = async (table) => {
    const p = new URLSearchParams(range);
    p.set("table", table);
    p.set("basis", basis);
    const r = await api.get(`/logistics/analysis.xlsx?${p.toString()}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `cost_${table}_${basis === "weight" ? "per_kg" : "per_unit"}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };
  const dlBtnStyle = { display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", border: "1px solid #CBD5E1", borderRadius: 7, background: "#fff", cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#0F172A" };
  const custCols = [
    { key: "pcode", header: "Customer", sortable: true, render: (r) => `${r.pcode} · ${r.customer_name || ""}`, searchAccessor: (r) => `${r.pcode} ${r.customer_name || ""}` },
    { key: "shipments", header: "Shipments", align: "right", sortable: true },
    { key: "total_inr", header: "Freight (₹)", align: "right", sortable: true, render: (r) => inr(r.total_inr) },
    { key: "duty_inr", header: "Duty (₹)", align: "right", sortable: true, render: (r) => inr(r.duty_inr) },
    { key: "landed_inr", header: "Landed (₹)", align: "right", sortable: true, render: (r) => inr(r.landed_inr) },
    { key: "sales_value_native", header: "Sales Value", align: "right", sortable: true, render: (r) => money(r.sales_value_native, r.sales_currency) },
    byWeightCust
      ? { key: "weight", header: "Weight (kg)", align: "right", sortable: true, render: (r) => r.weight ? r.weight.toLocaleString() : "—" }
      : { key: "units", header: "Units", align: "right", sortable: true, render: (r) => r.units?.toLocaleString() },
    byWeightCust
      ? { key: "per_kg_inr", header: "Freight ₹/kg (avg)", align: "right", sortable: true, render: (r) => inr(r.per_kg_inr) }
      : { key: "per_unit_inr", header: "Freight ₹/unit (avg)", align: "right", sortable: true, render: (r) => inr(r.per_unit_inr) },
  ];
  const shipCols = [
    { key: "inv_no", header: "Invoice", sortable: true, render: (r) => <button onClick={() => setPkSale(r.sale_no)} data-testid={`logistics-ship-inv-${r.sale_no}`} title="View packing list" style={{ color: "#0EA5E9", fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>#{r.inv_no}</button> },
    { key: "sale_date", header: "Invoice Date", type: "date", sortable: true, render: (r) => r.sale_date || "—" },
    { key: "customer_name", header: "Customer", sortable: true },
    { key: "shipment_type", header: "Type", sortable: true },
    { key: "destination", header: "Destination", sortable: true, render: (r) => r.destination || "—" },
    { key: "total_inr", header: "Freight (₹)", align: "right", sortable: true, render: (r) => inr(r.total_inr) },
    { key: "duty_inr", header: "Duty (₹)", align: "right", sortable: true, render: (r) => inr(r.duty_inr) },
    { key: "landed_inr", header: "Landed (₹)", align: "right", sortable: true, render: (r) => inr(r.landed_inr) },
    { key: "sales_value_native", header: "Sales Value", align: "right", sortable: true, render: (r) => money(r.sales_value_native, r.sales_currency) },
    byWeightCust
      ? { key: "weight", header: "Weight (kg)", align: "right", sortable: true, render: (r) => r.weight ? r.weight.toLocaleString() : "—" }
      : { key: "units", header: "Units", align: "right", sortable: true, render: (r) => r.units?.toLocaleString() },
    byWeightCust
      ? { key: "per_kg_inr", header: "Freight ₹/kg", align: "right", sortable: true, render: (r) => inr(r.per_kg_inr) }
      : { key: "per_unit_inr", header: "Freight ₹/unit", align: "right", sortable: true, render: (r) => inr(r.per_unit_inr) },
  ];
  const dateInput = { padding: "7px 9px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 };
  return (
    <div data-testid="logistics-analysis">
      {/* period selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>Invoice date:</span>
        <input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} data-testid="logistics-period-from" style={dateInput} />
        <span style={{ color: "#94A3B8" }}>→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="logistics-period-to" style={dateInput} />
        {(frm || to) && <button onClick={() => { setFrm(""); setTo(""); }} data-testid="logistics-period-clear" className="jde-btn" style={{ fontSize: 12 }}>Clear</button>}
        <button onClick={() => setMwOpen(true)} data-testid="logistics-missing-weight-btn" className="jde-btn"
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#B45309", borderColor: "#FED7AA" }}>
          <Scales size={14} weight="bold" /> Missing weight report
        </button>
      </div>
      <div style={{ display: "flex", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        <Card testid="logistics-card-shipments" label="Shipments" value={data.totals.shipments || 0} />
        <Card testid="logistics-card-freight" label="Total Freight" value={inr(data.totals.total_inr)} value2={usd(data.totals.total_usd)} />
        <Card testid="logistics-card-duty" label="Total Duty" value={inr(data.totals.duty_inr)} value2={usd(data.totals.duty_usd)} />
        <Card testid="logistics-card-landed" label="Landed Cost (Freight + Duty)" value={inr(data.totals.landed_inr)} value2={usd(data.totals.landed_usd)} highlight />
        {byWeightCust && data.totals.total_weight > 0 && (
          <Card testid="logistics-card-avg-kg" label="Avg Freight / kg" value={inr(data.totals.total_inr / data.totals.total_weight)} value2={usd(data.totals.total_usd / data.totals.total_weight)} />
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 8px", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Cost by Item {byWeight ? "(per kg)" : "(per unit)"}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => dlXlsx("item")} data-testid="logistics-dl-item" style={dlBtnStyle}>
            <DownloadSimple size={14} weight="bold" /> XLSX
          </button>
          <div style={{ display: "inline-flex", border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }} data-testid="logistics-basis-toggle">
            {["value", "weight"].map((b) => (
              <button key={b} onClick={() => setBasis(b)} data-testid={`logistics-basis-${b}`}
                style={{ padding: "6px 14px", border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, background: basis === b ? "#0EA5E9" : "#fff", color: basis === b ? "#fff" : "#475569" }}>
                By {b}
              </button>
            ))}
          </div>
        </div>
      </div>
      {items && basis === "weight" && items.basis === "value" && (
        <div style={{ fontSize: 11.5, color: "#B45309", background: "#FFFBEB", border: "1px solid #FEF3C7", borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
          No item weights found for these shipments — showing value-based allocation instead.
        </div>
      )}
      <div style={{ marginBottom: 24 }}>
        <SortableGrid columns={itemCols} rows={items ? items.items : []} testid="logistics-item-grid"
          getRowKey={(r) => r.part} searchPlaceholder="Search item…" emptyMessage="No costs captured for this period." />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 8px" }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>By Customer {byWeightCust ? "(per kg)" : "(per unit)"}</h3>
        <button onClick={() => dlXlsx("customer")} data-testid="logistics-dl-customer" style={dlBtnStyle}>
          <DownloadSimple size={14} weight="bold" /> XLSX
        </button>
      </div>
      {basis === "weight" && !data.weight_available && (
        <div style={{ fontSize: 11.5, color: "#B45309", background: "#FFFBEB", border: "1px solid #FEF3C7", borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
          No item weights found for these shipments — showing per-unit figures instead.
        </div>
      )}
      <div style={{ marginBottom: 24 }}>
        <SortableGrid columns={custCols} rows={data.by_customer || []} testid="logistics-cust-grid"
          getRowKey={(r) => r.pcode} searchPlaceholder="Search customer…" emptyMessage="No costs captured for this period." />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 8px" }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>By Shipment {byWeightCust ? "(per kg)" : "(per unit)"}</h3>
        <button onClick={() => dlXlsx("shipment")} data-testid="logistics-dl-shipment" style={dlBtnStyle}>
          <DownloadSimple size={14} weight="bold" /> XLSX
        </button>
      </div>
      <SortableGrid columns={shipCols} rows={data.shipments || []} testid="logistics-ship-grid"
        getRowKey={(r) => r.sale_no} searchPlaceholder="Search shipment…" emptyMessage="No costs captured for this period." />
      <PackingListModal saleNo={pkSale} onClose={() => setPkSale(null)} />
      <MissingWeightModal open={mwOpen} onClose={() => setMwOpen(false)} range={range} />
    </div>
  );
}

const pth = { textAlign: "left", fontSize: 11, color: "#64748B", fontWeight: 700, padding: "7px 9px", borderBottom: "1px solid #E2E8F0", textTransform: "uppercase" };
const ptd = { padding: "7px 9px", fontSize: 13, borderBottom: "1px solid #F1F5F9" };
function PackingListModal({ saleNo, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (saleNo == null) { setD(null); return; }
    setD(null);
    api.get(`/logistics/packing-list/${saleNo}`).then((r) => setD(r.data)).catch(() => setD({ lines: [] }));
  }, [saleNo]);
  if (saleNo == null) return null;
  return (
    <div onClick={onClose} data-testid="packing-list-modal" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "min(720px,94vw)", maxHeight: "85vh", overflow: "auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Packing List{d && d.inv_no ? ` · #${d.inv_no}` : ""}</h3>
          <button onClick={onClose} data-testid="packing-list-close" className="jde-btn">Close</button>
        </div>
        {!d ? <div className="muted">Loading…</div> : (
          <>
            <div style={{ fontSize: 12.5, color: "#64748B", marginBottom: 10 }}>
              {d.customer_name} · {d.sale_date} · Total {d.total_qty?.toLocaleString()} pcs · <b>{d.total_weight_kg?.toLocaleString()} kg</b>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={pth}>Item</th><th style={{ ...pth, textAlign: "right" }}>Qty</th><th style={{ ...pth, textAlign: "right" }}>Unit wt (kg)</th><th style={{ ...pth, textAlign: "right" }}>Line wt (kg)</th></tr></thead>
              <tbody>{(d.lines || []).map((l, i) => (
                <tr key={i}><td style={ptd}>{l.part}{l.name ? ` · ${l.name}` : ""}</td>
                  <td style={{ ...ptd, textAlign: "right" }}>{l.qty?.toLocaleString()}</td>
                  <td style={{ ...ptd, textAlign: "right" }}>{l.unit_weight || "—"}</td>
                  <td style={{ ...ptd, textAlign: "right" }}>{l.line_weight ? l.line_weight.toLocaleString() : "—"}</td></tr>
              ))}</tbody>
            </table>
            {d.total_weight_kg === 0 && <div style={{ fontSize: 11.5, color: "#B45309", marginTop: 8 }}>No item weights found in the ERP item master for these codes.</div>}
          </>
        )}
      </div>
    </div>
  );
}
function MissingWeightModal({ open, onClose, range }) {
  const [rep, setRep] = useState(null);
  const [dl, setDl] = useState(false);
  useEffect(() => {
    if (!open) { setRep(null); return; }
    setRep(null);
    api.get(`/logistics/missing-weights${range ? "?" + range : ""}`)
      .then((r) => setRep(r.data)).catch(() => setRep({ items: [], missing_count: 0 }));
  }, [open, range]);
  const download = async () => {
    setDl(true);
    try {
      const r = await api.get(`/logistics/missing-weights.xlsx${range ? "?" + range : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `missing_weights_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); } finally { setDl(false); }
  };
  if (!open) return null;
  return (
    <div onClick={onClose} data-testid="missing-weight-modal" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "min(760px,96vw)", maxHeight: "85vh", overflow: "auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Items Missing ERP Weight</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={download} disabled={dl || !rep || !rep.missing_count} data-testid="missing-weight-download"
              className="jde-btn jde-btn-primary" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <DownloadSimple size={14} weight="bold" /> {dl ? "Preparing…" : "Download (Excel)"}
            </button>
            <button onClick={onClose} data-testid="missing-weight-close" className="jde-btn">Close</button>
          </div>
        </div>
        <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 12px" }}>
          These item codes have no unit weight in the ERP item master, so the <b>By weight</b> analysis
          falls back to value-based allocation for them. Add weights in the ERP to get accurate ₹/kg.
        </p>
        {!rep ? <div className="muted" style={{ padding: 20 }}>Loading…</div> : (
          <>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 12, fontSize: 12.5, color: "#334155" }} data-testid="missing-weight-summary">
              <span><b style={{ fontSize: 15, color: rep.missing_count ? "#B45309" : "#059669" }}>{rep.missing_count}</b> of {rep.total_item_codes} item codes missing weight</span>
              <span><b style={{ fontSize: 15, color: "#0F172A" }}>{rep.shipments_affected}</b> of {rep.total_shipments} shipments affected</span>
            </div>
            {rep.missing_count === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#059669", fontWeight: 700, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10 }}>
                All items in this period have weights in the ERP.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={pth}>Item</th>
                  <th style={{ ...pth, textAlign: "right" }}>Total Qty</th>
                  <th style={{ ...pth, textAlign: "right" }}>Shipments</th>
                  <th style={pth}>Invoices</th>
                </tr></thead>
                <tbody>{rep.items.map((r, i) => (
                  <tr key={i} data-testid={`missing-weight-row-${r.part}`}>
                    <td style={ptd}>{r.part}{r.name ? ` · ${r.name}` : ""}</td>
                    <td style={{ ...ptd, textAlign: "right" }}>{r.qty?.toLocaleString()}</td>
                    <td style={{ ...ptd, textAlign: "right" }}>{r.shipments}</td>
                    <td style={{ ...ptd, fontSize: 11.5, color: "#64748B" }}>{(r.invoices || []).map((x) => `#${x}`).join(", ")}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const Card = ({ label, value, value2, highlight, testid }) => (
  <div data-testid={testid} style={{ flex: 1, minWidth: 150, padding: "14px 18px", borderRadius: 12,
    border: highlight ? "1px solid #0EA5E9" : "1px solid #E2E8F0",
    background: highlight ? "#F0F9FF" : "#fff" }}>
    <div style={{ fontSize: 11, color: highlight ? "#0369A1" : "#94A3B8", fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", marginTop: 4 }}>{value}</div>
    {value2 !== undefined && (
      <div style={{ fontSize: 13, fontWeight: 700, color: highlight ? "#0369A1" : "#64748B", marginTop: 2 }}>{value2}</div>
    )}
  </div>
);

export default function Logistics() {
  const loc = useLocation();
  const tab = loc.pathname.includes("/analysis") ? "analysis" : "capture";
  return (
    <Layout breadcrumb={["Logistics", tab === "analysis" ? "Cost Analysis" : "Capture Shipment Costs"]}>
      <div data-testid="logistics-page" style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <CurrencyDollar size={22} weight="bold" color="#0EA5E9" />
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#0F172A" }}>
            {tab === "analysis" ? "Logistics Cost Analysis" : "Capture Shipment Logistics Costs"}
          </h1>
        </div>
        {tab === "analysis" ? <AnalysisTab /> : <CaptureTab />}
      </div>
    </Layout>
  );
}
